import { useCallback, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { useNavigate } from '@tanstack/react-router';
import {
  BookingChannels,
  ScheduleDataPoint,
  ScheduleRequestReviewStatus,
  TimeFilterType,
} from '@weave/schema-gen-ts/dist/schemas/analytics-reporting/analytics_gateway/analytics_gateway.pb';
import dayjs from 'dayjs';
import { AnalyticsGatewayV1 } from '@frontend/api-analytics-gateway';
import { DataCard, DataCardProps, TimeBasedBarChart } from '@frontend/analytics-shared';
import { i18next, useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Heading, Text, Truncated } from '@frontend/design-system';
import { APPOINTMENT_TABS, scheduleRequestsYAxisGroups } from '../constants';
import { useAppointmentAnalyticsV2ShallowStore } from '../hooks/use-appointment-analytics-v2-store';
import { ScheduleRequestStackKeys } from '../types';

const stackKeys = [
  ScheduleRequestStackKeys.Accepted,
  ScheduleRequestStackKeys.Pending,
  ScheduleRequestStackKeys.Deleted,
  ScheduleRequestStackKeys.Done,
  ScheduleRequestStackKeys.Messaged,
  ScheduleRequestStackKeys.Rejected,
  ScheduleRequestStackKeys.Called,
  ScheduleRequestStackKeys.Dismissed,
];

const colors: Record<string, string> = {
  [ScheduleRequestStackKeys.Accepted]: theme.colors.success30,
  [ScheduleRequestStackKeys.Pending]: theme.colors.warning20,
  [ScheduleRequestStackKeys.Done]: theme.colors.secondary.seaweed30,
  [ScheduleRequestStackKeys.Messaged]: theme.colors.primary20,
  [ScheduleRequestStackKeys.Rejected]: theme.colors.rose30,
  [ScheduleRequestStackKeys.Called]: theme.colors.secondary.eggplant30,
  [ScheduleRequestStackKeys.Dismissed]: theme.colors.neutral30,
  [ScheduleRequestStackKeys.Deleted]: theme.colors.critical30,
};

const strokeColors: Record<string, string> = {
  [ScheduleRequestStackKeys.Accepted]: theme.colors.success50,
  [ScheduleRequestStackKeys.Pending]: theme.colorsV2.yellow50(),
  [ScheduleRequestStackKeys.Done]: theme.colors.secondary.seaweed50,
  [ScheduleRequestStackKeys.Messaged]: theme.colors.primary50,
  [ScheduleRequestStackKeys.Rejected]: theme.colors.rose50,
  [ScheduleRequestStackKeys.Called]: theme.colors.secondary.eggplant50,
  [ScheduleRequestStackKeys.Dismissed]: theme.colors.neutral50,
  [ScheduleRequestStackKeys.Deleted]: theme.colors.critical50,
};

const labels: Record<string, string> = {
  [ScheduleRequestStackKeys.Accepted]: i18next.t('Accepted'),
  [ScheduleRequestStackKeys.Pending]: i18next.t('Pending'),
  [ScheduleRequestStackKeys.Done]: i18next.t('Done'),
  [ScheduleRequestStackKeys.Messaged]: i18next.t('Messaged'),
  [ScheduleRequestStackKeys.Rejected]: i18next.t('Rejected'),
  [ScheduleRequestStackKeys.Called]: i18next.t('Called'),
  [ScheduleRequestStackKeys.Dismissed]: i18next.t('Dismissed'),
  [ScheduleRequestStackKeys.Deleted]: i18next.t('Deleted'),
};

const stackKeyToStatus: Record<string, ScheduleRequestReviewStatus> = {
  [ScheduleRequestStackKeys.Accepted]: ScheduleRequestReviewStatus.SCHEDULE_REQUEST_REVIEW_STATUS_ACCEPTED,
  [ScheduleRequestStackKeys.Pending]: ScheduleRequestReviewStatus.SCHEDULE_REQUEST_REVIEW_STATUS_PENDING,
  [ScheduleRequestStackKeys.Deleted]: ScheduleRequestReviewStatus.SCHEDULE_REQUEST_REVIEW_STATUS_DELETED,
  [ScheduleRequestStackKeys.Done]: ScheduleRequestReviewStatus.SCHEDULE_REQUEST_REVIEW_STATUS_DONE,
  [ScheduleRequestStackKeys.Messaged]: ScheduleRequestReviewStatus.SCHEDULE_REQUEST_REVIEW_STATUS_MESSAGED,
  [ScheduleRequestStackKeys.Rejected]: ScheduleRequestReviewStatus.SCHEDULE_REQUEST_REVIEW_STATUS_REJECTED,
  [ScheduleRequestStackKeys.Called]: ScheduleRequestReviewStatus.SCHEDULE_REQUEST_REVIEW_STATUS_CALLED,
  [ScheduleRequestStackKeys.Dismissed]: ScheduleRequestReviewStatus.SCHEDULE_REQUEST_REVIEW_STATUS_DISMISSED,
};

type ScheduleRequestsYAxes = {
  channel: BookingChannels[];
};

const defaultYAxisSelected: ScheduleRequestsYAxes = {
  channel: [],
};

interface ScheduleRequestsChartProps {
  tab: APPOINTMENT_TABS;
}

export const ScheduleRequestsChart = ({ tab }: ScheduleRequestsChartProps) => {
  const { t } = useTranslation('analytics');
  const navigate = useNavigate();

  const { filtersInitialized, filters, filterHintText } = useAppointmentAnalyticsV2ShallowStore(
    'filtersInitialized',
    'filters',
    'filterHintText'
  );
  const [selectedXAxis, setSelectedXAxis] = useState<TimeFilterType>(TimeFilterType.DAILY);
  const [selectedYAxes, setSelectedYAxes] = useState<ScheduleRequestsYAxes>(defaultYAxisSelected);
  const isChartFilterApplied = selectedYAxes.channel.length > 0 || selectedXAxis !== TimeFilterType.DAILY;

  const currentFilters = filters[tab];
  const isEnabled = filtersInitialized && (currentFilters?.locationId?.length ?? 0) > 0;

  const { data: aggregatesData, isLoading: aggregatesLoading } =
    AnalyticsGatewayV1.Queries.useGetScheduleAggregatesQuery(
      {
        ...currentFilters,
        channel: selectedYAxes.channel,
      },
      {
        enabled: isEnabled,
      }
    );

  const { data: scheduleData, isLoading: chartLoading } = AnalyticsGatewayV1.Queries.useGetScheduleDataQuery(
    {
      ...currentFilters,
      filterType: selectedXAxis,
      channel: selectedYAxes.channel,
    },
    {
      enabled: isEnabled,
    }
  );

  const totalScheduledRequests = Number(aggregatesData?.totalScheduledRequests) ?? 0;

  // Adapt the flat schedule data points into the {name, values[]} shape TimeBasedBarChart expects.
  const chartResponse = useMemo(
    () => ({
      data: (scheduleData?.data ?? []).map((point: ScheduleDataPoint) => ({
        name: point.name ?? '',
        values: stackKeys.map((key) => ({ key, value: Number(point[key] ?? 0) })),
      })),
    }),
    [scheduleData]
  );

  const goToScheduleRequestsView = useCallback(
    (params: { status?: ScheduleRequestReviewStatus; startDate?: string; endDate?: string } = {}) => {
      navigate({
        from: '/analytics/appointment',
        to: '/analytics/appointment',
        search: (prev: any) => ({
          ...prev,
          customTableView: 'schedule-requests',
          filters: params,
        }),
      });
    },
    [navigate]
  );

  const cardsData: DataCardProps[] = useMemo(() => {
    const totalScheduled = Number(aggregatesData?.totalScheduledRequests) ?? 0;
    const totalAcceptedRequests = Number(aggregatesData?.totalAcceptedRequests) ?? 0;
    const totalPendingRequests = Number(aggregatesData?.totalPendingRequests) ?? 0;
    const totalRejectedRequests = Number(aggregatesData?.totalRejectedRequests) ?? 0;

    return [
      {
        label: t('Total Requests'),
        value: totalScheduled,
        isLoading: aggregatesLoading,
        iconData: {
          name: 'calendar-add',
          color: theme.colors.primary60,
          bgColor: theme.colors.primary5,
        },
        ctaConfig: {
          icon: 'trend-up',
          text: t('View'),
          onClick: () => goToScheduleRequestsView(),
          disabled: totalScheduled === 0,
        },
      },
      {
        label: t('Accepted'),
        value: totalAcceptedRequests,
        isLoading: aggregatesLoading,
        iconData: {
          name: 'check',
          color: theme.colors.success50,
          bgColor: theme.colors.success5,
        },
        ctaConfig: {
          icon: 'trend-up',
          text: t('View'),
          onClick: () =>
            goToScheduleRequestsView({ status: ScheduleRequestReviewStatus.SCHEDULE_REQUEST_REVIEW_STATUS_ACCEPTED }),
          disabled: totalAcceptedRequests === 0,
        },
      },
      {
        label: t('Rejected'),
        value: totalRejectedRequests,
        isLoading: aggregatesLoading,
        iconData: {
          name: 'x',
          color: theme.colors.critical50,
          bgColor: theme.colors.critical5,
        },
        ctaConfig: {
          icon: 'trend-up',
          text: t('View'),
          onClick: () =>
            goToScheduleRequestsView({ status: ScheduleRequestReviewStatus.SCHEDULE_REQUEST_REVIEW_STATUS_REJECTED }),
          disabled: totalRejectedRequests === 0,
        },
      },
      {
        label: t('Pending'),
        value: totalPendingRequests,
        isLoading: aggregatesLoading,
        iconData: {
          name: 'pending',
          color: theme.colors.neutral70,
          bgColor: theme.colors.neutral10,
        },
        ctaConfig: {
          icon: 'trend-up',
          text: t('View'),
          onClick: () =>
            goToScheduleRequestsView({ status: ScheduleRequestReviewStatus.SCHEDULE_REQUEST_REVIEW_STATUS_PENDING }),
          disabled: totalPendingRequests === 0,
        },
      },
    ] as DataCardProps[];
  }, [aggregatesData, aggregatesLoading, goToScheduleRequestsView, t]);

  const subtitle = filterHintText
    ? `${filterHintText} ${t('of')} ${t(
        'appointments'
      )}, Provider and Contact Type filters are not applicable to this chart`
    : undefined;
  const chartTitle = t('Schedule Requests');

  const chart = (
    <TimeBasedBarChart
      data={chartResponse}
      isLoading={chartLoading}
      seriesConfig={{ mode: 'stacked', seriesKeys: [...stackKeys] }}
      colors={colors}
      strokeColors={strokeColors}
      labels={labels}
      xAxis={{ selected: selectedXAxis, defaultSelected: TimeFilterType.DAILY, onChange: setSelectedXAxis }}
      yAxis={{
        selected: selectedYAxes,
        defaultSelected: defaultYAxisSelected,
        onChange: (yAxes) => setSelectedYAxes(yAxes as ScheduleRequestsYAxes),
        groups: scheduleRequestsYAxisGroups,
      }}
      appearance={{ mode: 'stacked', barSize: 24, barRadius: 4, hideMarkers: true, collectiveTooltip: false }}
      chartHeight={240}
      onBarClick={({ startDate, endDate, barId }) => {
        const status = stackKeyToStatus[barId];
        goToScheduleRequestsView({
          startDate: dayjs(startDate).startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS'),
          endDate: dayjs(endDate).endOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS'),
          status,
        });
      }}
      legends={{ doNotCollapseLegends: true }}
      style={{ marginTop: theme.spacing(0) }}
    />
  );

  // If there are no scheduled requests, do not render the chart section.
  if (totalScheduledRequests === 0 && !isChartFilterApplied) {
    return null;
  }

  return (
    <section css={styles.outerContainer}>
      <Heading
        level={3}
        css={{
          lineHeight: theme.spacing(3),
        }}
      >
        {chartTitle}
        {subtitle && (
          <Truncated
            weight='light'
            color='subdued'
            as={Text}
            size='medium'
            css={{ maxWidth: '700px', marginTop: theme.spacing(0.5) }}
          >
            {subtitle}
          </Truncated>
        )}
      </Heading>

      <div css={styles.cardsGrid}>
        {cardsData.map((card) => (
          <DataCard key={card.label} {...card} />
        ))}
      </div>

      {chart}
    </section>
  );
};

const styles = {
  outerContainer: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
    padding: ${theme.spacing(2)};
    border: 1px solid ${theme.colors.neutral20};
    border-radius: ${theme.spacing(1.5)};
  `,
  cardsGrid: css`
    display: flex;
    flex-wrap: wrap;
    gap: ${theme.spacing(2)};
  `,
};
